// frontend/app/components/lessonPlans/lessonQuizGrading.js
//
// Grading one bank question against its authored key. Pure — no React, no
// network — because this is the piece that decides what `is_correct` a lesson
// records, and that number ends up in a mastery bar and on a teacher's screen.
//
// The rules are the ones `computeAnswerFeedback` in AITutorApp.js applies to
// the same questions in the global quiz player, deliberately: a student who
// answers question 40213 inside a lesson must be marked exactly as a student
// who answers it from the topic tile. They are duplicated rather than imported
// only because `computeAnswerFeedback` is a closure inside a 30k-line
// component; hoisting it into this module and having AITutorApp call it is the
// follow-up, and this file is written to be that module.
//
// Question types, verified against quiz_content.db:
//   1 = fill in the blank   2 = MCQ (single)
//   3 = true / false        4 = multi-select (pipe-delimited correct_ans)
//   7 = descriptive         (never served to a lesson — see lesson_quiz.py)

const normalize = (value) =>
  value == null ? "" : String(value).trim().toLowerCase();

/**
 * "option3" | "3" | " Option 3 " -> 3. The bank stores a correct answer as a
 * 1-based index while the UI works in option keys, so everything is compared
 * as a number.
 */
export const optionIndex = (value) => {
  const text = normalize(value);
  if (!text) return null;
  const stripped = text.startsWith("option") ? text.slice(6) : text;
  const digits = stripped.match(/\d+/);
  return digits ? parseInt(digits[0], 10) : null;
};

/** "1|3" | ["option1","option3"] -> Set {1, 3}. */
export const indexSet = (raw) => {
  const parts = Array.isArray(raw) ? raw : String(raw ?? "").split(/[|,]/);
  return new Set(parts.map(optionIndex).filter((index) => index != null));
};

/** Is there an answer here at all? An empty selection is unanswered, not wrong. */
export const hasQuizAnswer = (answer) => {
  if (answer === null || answer === undefined) return false;
  if (Array.isArray(answer)) return answer.filter(Boolean).length > 0;
  return String(answer).trim().length > 0;
};

/**
 * Grade one answer.
 *
 * Returns `true`, `false`, or `null` — and `null` is a real third answer, not a
 * failure. It means "nobody can mark this": an unattempted question, or a type
 * with no machine-checkable key. Recording those as `false` is what once made a
 * concept read 0% for a class that had simply not been asked yet.
 */
export const gradeQuizAnswer = (question, answer) => {
  if (!question || !hasQuizAnswer(answer)) return null;

  const type = parseInt(question.question_type, 10);
  const key = question.options?.correct_answer;

  switch (type) {
    case 2: {
      const correct = optionIndex(key);
      const chosen = optionIndex(answer);
      if (correct == null || chosen == null) return null;
      return chosen === correct;
    }

    case 3: {
      // True/False is stored MCQ-style (option1="True", correct_ans="1"), but
      // legacy rows hold the literal label instead. Try the index first and
      // fall back to comparing the words.
      const correct = optionIndex(key);
      const chosen = optionIndex(answer);
      if (correct != null && chosen != null) return chosen === correct;
      if (key == null) return null;
      return normalize(answer) === normalize(key);
    }

    case 4: {
      // Every correct option and no incorrect one — a partially right
      // selection is not partially right, it is a different claim.
      const correct = indexSet(key);
      const chosen = indexSet(answer);
      if (correct.size === 0) return null;
      return (
        correct.size === chosen.size && [...correct].every((n) => chosen.has(n))
      );
    }

    case 1: {
      if (key == null) return null;
      const typed = normalize(answer);
      if (Array.isArray(key)) {
        return key.some((accepted) => normalize(accepted) === typed);
      }
      return typed === normalize(key);
    }

    default:
      // Descriptive, matching, anything else: not auto-gradable. The lesson
      // records the answer and leaves is_correct null.
      return null;
  }
};

/** How many of `questions` have an answer, and how many of those are right. */
export const quizTally = (questions, answers) => {
  const list = Array.isArray(questions) ? questions : [];
  let answered = 0;
  let correct = 0;

  list.forEach((question) => {
    const answer = answers?.[question?.question_id];
    if (!hasQuizAnswer(answer)) return;
    answered += 1;
    if (gradeQuizAnswer(question, answer) === true) correct += 1;
  });

  return { total: list.length, answered, correct };
};
